export type ParentFinancialAidDocumentStatus = {
  id: string;
  name: string;
  uploaded: boolean;
  fileName?: string;
  // Absent means required; only explicitly-optional slots may stay empty at
  // submission (e.g. the catch-all "Other" upload).
  required?: boolean;
};

type FinancialAidDocumentDefinition = {
  id: string;
  name: string;
  defaultEnabled: boolean;
  optionalUpload?: boolean;
  legacyIds?: string[];
};

export const PARENT_FINANCIAL_AID_DOCUMENT_DEFINITIONS: FinancialAidDocumentDefinition[] = [
  { id: 'w2_forms', name: 'W-2 Forms', defaultEnabled: true },
  {
    id: 'tax_return_federal',
    name: 'Federal Tax Return',
    defaultEnabled: true,
    legacyIds: ['federal_tax_form_1040_2024', 'federal_tax_form_1040_2025'],
  },
  {
    id: 'tax_return_state',
    name: 'State Tax Return',
    defaultEnabled: true,
    legacyIds: ['state_tax_forms_2024', 'state_tax_forms_2025'],
  },
  { id: 'pay_stubs', name: 'Recent Pay Stubs', defaultEnabled: true },
  { id: 'bank_statements', name: 'Bank Statements', defaultEnabled: true },
  { id: 'investment_statements', name: 'Investment Statements', defaultEnabled: true },
  {
    id: 'business_tax_return',
    name: 'Business Tax Return',
    defaultEnabled: true,
    legacyIds: ['form_1065', 'form_1120s'],
  },
  { id: 'proof_of_benefits', name: 'Proof of Benefits', defaultEnabled: true },
  { id: 'mortgage_statement', name: 'Mortgage Statement', defaultEnabled: true },
  {
    id: 'other_income_proof',
    name: 'Other Income Proof',
    defaultEnabled: false,
    legacyIds: ['form_4506', 'other_scholarship_applications', 'other_scholarship_award_letters'],
  },
  {
    id: 'other',
    name: 'Other',
    defaultEnabled: false,
    optionalUpload: true,
  },
];

const PARENT_FINANCIAL_AID_DOCUMENT_ALIAS_TO_CANONICAL = new Map(
  PARENT_FINANCIAL_AID_DOCUMENT_DEFINITIONS.flatMap((definition): Array<readonly [string, string]> => [
    [definition.id, definition.id] as const,
    ...(definition.legacyIds ?? []).map((legacyId) => [legacyId, definition.id] as const),
  ]),
);

export const normalizeParentFinancialAidDocumentId = (value: unknown): string => {
  const id = readText(value);
  return PARENT_FINANCIAL_AID_DOCUMENT_ALIAS_TO_CANONICAL.get(id) ?? id;
};

const asRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
);

const readText = (value: unknown): string => String(value ?? '').trim();

const humanizeDocumentId = (value: string): string =>
  value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
    .join(' ');

const readSettingLabel = (record: Record<string, unknown>): string =>
  readText(record.label) || readText(record.name) || readText(record.title);

const readBooleanFlag = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const token = value.trim().toLowerCase();
    if (['true', 'yes', '1', 'enabled', 'required'].includes(token)) return true;
    if (['false', 'no', '0', 'disabled', 'optional'].includes(token)) return false;
  }
  return undefined;
};

const readSettingEnabled = (
  record: Record<string, unknown>,
  fallback: boolean,
): boolean => {
  const enabled =
    readBooleanFlag(record.enabled) ??
    readBooleanFlag(record.required) ??
    readBooleanFlag(record.isRequired) ??
    readBooleanFlag(record.isEnabled);
  return enabled ?? fallback;
};

const toDocumentStatus = (
  definition: FinancialAidDocumentDefinition,
  name = definition.name,
  required = !definition.optionalUpload,
): ParentFinancialAidDocumentStatus => ({
  id: definition.id,
  name,
  uploaded: false,
  required,
});

const readSettingOptionalUpload = (
  record: Record<string, unknown> | undefined,
  definition: FinancialAidDocumentDefinition,
): boolean => {
  const configured = record ? readBooleanFlag(record.optional) : undefined;
  return configured ?? Boolean(definition.optionalUpload);
};

export const getDefaultParentFinancialAidDocuments = (): ParentFinancialAidDocumentStatus[] =>
  PARENT_FINANCIAL_AID_DOCUMENT_DEFINITIONS
    .filter((definition) => definition.defaultEnabled)
    .map((definition) => toDocumentStatus(definition));

export const buildConfiguredParentFinancialAidDocuments = (requiredDocuments: unknown): ParentFinancialAidDocumentStatus[] => {
  if (!Array.isArray(requiredDocuments)) {
    return getDefaultParentFinancialAidDocuments();
  }

  const rows = requiredDocuments;
  if (rows.length === 0) {
    return [];
  }

  const settingsById = new Map<string, Record<string, unknown>>();
  rows.forEach((row) => {
    const record = asRecord(row);
    const id = readText(record.id);
    if (id) settingsById.set(id, record);
  });

  return PARENT_FINANCIAL_AID_DOCUMENT_DEFINITIONS.flatMap((definition) => {
    const canonicalSetting = settingsById.get(definition.id);
    const legacySettings = (definition.legacyIds ?? [])
      .map((id) => settingsById.get(id))
      .filter((record): record is Record<string, unknown> => Boolean(record));
    const enabledLegacySettings = legacySettings.filter((setting) => readSettingEnabled(setting, false));
    const labelSetting = canonicalSetting ?? enabledLegacySettings[0] ?? legacySettings[0];
    const settingLabel = labelSetting ? readSettingLabel(labelSetting) : '';
    const enabled = canonicalSetting
      ? readSettingEnabled(canonicalSetting, definition.defaultEnabled)
      : legacySettings.length > 0
        ? legacySettings.some((setting) => readSettingEnabled(setting, definition.defaultEnabled))
        : definition.defaultEnabled;
    if (!enabled) return [];
    return [toDocumentStatus(
      definition,
      settingLabel || definition.name,
      !readSettingOptionalUpload(canonicalSetting, definition),
    )];
  });
};

export const buildParentFinancialAidDocumentsFromRequiredTypes = (
  requiredDocumentTypes: unknown,
  configuredDocuments: ParentFinancialAidDocumentStatus[] = [],
): ParentFinancialAidDocumentStatus[] | null => {
  if (!Array.isArray(requiredDocumentTypes)) {
    return null;
  }

  const configuredById = new Map(configuredDocuments.map((document) => [document.id, document]));
  const seen = new Set<string>();
  const requiredDocuments = requiredDocumentTypes.flatMap((documentType) => {
    const normalizedId = normalizeParentFinancialAidDocumentId(documentType);
    if (!normalizedId || seen.has(normalizedId)) {
      return [];
    }
    const definition = PARENT_FINANCIAL_AID_DOCUMENT_DEFINITIONS.find((candidate) =>
      candidate.id === normalizedId,
    );
    if (!definition) {
      return [];
    }
    seen.add(normalizedId);
    const configured = configuredById.get(normalizedId);
    return [toDocumentStatus(
      definition,
      configured?.name || definition.name,
      configured?.required ?? !definition.optionalUpload,
    )];
  });
  const optionalDocuments = configuredDocuments.flatMap((document) => {
    const normalizedId = normalizeParentFinancialAidDocumentId(document.id);
    if (!normalizedId || seen.has(normalizedId) || document.required !== false) {
      return [];
    }
    seen.add(normalizedId);
    return [{
      ...document,
      uploaded: false,
      fileName: undefined,
      required: false,
    }];
  });
  return [...requiredDocuments, ...optionalDocuments];
};

export const resolveParentFinancialAidDocumentName = (
  value: unknown,
  configuredDocuments: ParentFinancialAidDocumentStatus[] = [],
): string => {
  const record = asRecord(value);
  const explicitLabel = readText(record.label) || readText(record.name) || readText(record.title);
  if (explicitLabel) return explicitLabel;

  const id =
    readText(record.documentType)
    || readText(record.documentId)
    || readText(record.id)
    || readText(record.type)
    || readText(record.key)
    || (value && typeof value === 'object' ? '' : readText(value));
  if (!id) return '';

  const normalizedId = normalizeParentFinancialAidDocumentId(id);
  const configuredDocument = configuredDocuments.find((document) => document.id === normalizedId);
  if (configuredDocument?.name) return configuredDocument.name;

  const definition = PARENT_FINANCIAL_AID_DOCUMENT_DEFINITIONS.find((candidate) =>
    candidate.id === normalizedId,
  );
  return definition?.name || humanizeDocumentId(id);
};

export const reconcileParentFinancialAidDocuments = (
  currentDocuments: ParentFinancialAidDocumentStatus[],
  configuredDocuments: ParentFinancialAidDocumentStatus[],
): ParentFinancialAidDocumentStatus[] => {
  const currentById = new Map(currentDocuments.map((document) => [document.id, document]));
  return configuredDocuments.map((document) => {
    const current = currentById.get(document.id);
    if (!current) return { ...document };
    return {
      ...document,
      uploaded: current.uploaded,
      fileName: current.fileName,
    };
  });
};

export const areParentFinancialAidDocumentsEqual = (
  left: ParentFinancialAidDocumentStatus[],
  right: ParentFinancialAidDocumentStatus[],
): boolean => left.length === right.length && left.every((document, index) => {
  const candidate = right[index];
  return Boolean(candidate)
    && document.id === candidate.id
    && document.name === candidate.name
    && document.uploaded === candidate.uploaded
    && document.fileName === candidate.fileName
    && (document.required ?? true) === (candidate.required ?? true);
});

export const areRequiredParentFinancialAidDocumentsUploaded = (
  documents: ParentFinancialAidDocumentStatus[],
): boolean => documents.every((document) => document.required === false || document.uploaded);
